import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Space, Tooltip} from 'antd';
import {connect} from 'react-redux';
import {StyledJournalEntryTabContainer} from '../../JournalEntriesTabs/StyledJournalEntriesTabs';
import {
  StyledMissingErpTabIcon,
  StyledMissingErpTabIconsSpace
} from '../../../NetSuiteBillsPage/tabs/MissingErpTab/StyledMissingErpTab';
import EmptyOrTable from '../../../../EmptyOrTable';
import MissingInfo from '../../../../MissingInfo';
import TableFilters from '../../../InvoicesPage/tabComponents/TableFilters';
import {accountingHelpers, invoicesHelpers} from '../../../../../utils/helpers';
import {TrashIcon} from '../../../../../icons';
import {useAccountingOptions} from '../../../../../hooks';
import {expenseTypesConstants, journalEntryStatusesConstants} from '../../../../../constants';

const dateColumnProps = accountingHelpers.getDateColumnProps();

const JournalEntryReviewTab = ({
  data = [],
  empty,
  loading = false,
  onAttachmentRemove,
  onFilter,
  onRowUpdate,
  tableFiltersProps,
  tableProps,

  dispatch,
  employees,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [query, setQuery] = useState({ search: '' });

  const {
    expenseCategoryOptions,
    departmentOptions,
    locationOptions,
  } = useAccountingOptions();

  const handleOnFilter = (filterParams) => {
    setQuery({...query, ...filterParams});
    onFilter?.(filterParams);
  }

  const getSelect = ({ defaultValue, options, propName, record }) =>
    accountingHelpers.getTableSelect({
      defaultValue,
      options,
      propName,
      record,
      t,
      onSelect: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
      ...rest
    });

  const getInput = ({ defaultValue, propName, record }) =>
    accountingHelpers.getTableInput({
      defaultValue,
      propName,
      record,
      t,
      onChange: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
    });

  const getVendor = (defaultValue, record) => {
    if (!invoicesHelpers.isExpenseInvoiceType(record?.expense)) return '';
    return accountingHelpers.getVendorName({exist: Boolean(record.vendor_id), data: record, t});
  }

  const handleRemoveAttachment = (e, id) => {
    e.stopPropagation();
    onAttachmentRemove?.(e, id);
  }

  const getInvoice = (value, record) => {
    const isReceiptExpense = record?.expense?.expense_type === expenseTypesConstants.RECEIPT;
    const invoiceFileUrl = record?.invoice_file_url;

    if (!value && (isReceiptExpense && !invoiceFileUrl)) return '';

    if (accountingHelpers.isMissingInvoice(record)) {
      return <MissingInfo description={`${t('invoice')} ${t('missing')}`} />;
    }

    return accountingHelpers.renderJEInvoicePreview(record);
  };

  const getTransactionDate = (date, record) => {
    const isReadyForReview = record?.status === journalEntryStatusesConstants.READY_FOR_REVIEW;
    return (
      <StyledMissingErpTabIconsSpace size='small' align='center'>
        {accountingHelpers.getEntryTransactionDate(date, record)}
        <Space className='ant-table-cell-actions' size='small' wrap={false}>
          {isReadyForReview && (
            <Tooltip title={`${t('remove')} ${t('attachment')}`}>
              <StyledMissingErpTabIcon onClick={(e) => handleRemoveAttachment(e, record.id)}>
                <TrashIcon />
              </StyledMissingErpTabIcon>
            </Tooltip>
          )}
          {accountingHelpers.getInvoiceLinkIcon({id: record?.invoice_id, t})}
        </Space>
      </StyledMissingErpTabIconsSpace>
    );
  }

  const columns = [
    {
      ...dateColumnProps,
      dataIndex: 'transaction_date',
      render: getTransactionDate,
      title: t('date')
    },
    {
      align: 'right',
      dataIndex: 'amount',
      render: (_, record) => accountingHelpers.getEntryAmount(record),
      title: t('amount'),
    },
    {
      dataIndex: 'card_public_token',
      render: accountingHelpers.getEntryCardId,
      title: `${t('card')} ID`
    },
    {
      dataIndex: 'invoice_id',
      render: getInvoice,
      title: t('invoice')
    },
    {
      dataIndex: 'card_owner_user_id',
      render: (value) => accountingHelpers.getOwnerDetails({ value, employees }),
      title: t('Owner')
    },
    {
      dataIndex: 'vendor_id',
      render: getVendor,
      title: t('vendor')
    },
    {
      dataIndex: 'memo',
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'memo', record }),
      title: t('memo')
    },
    {
      dataIndex: 'department',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: departmentOptions, propName: 'department' }),
      title: t('department'),
    },
    {
      dataIndex: 'debit_account',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: expenseCategoryOptions, propName: 'debit_account' }),
      title: t('debitAccount'),
    },
    {
      dataIndex: 'credit_account',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: expenseCategoryOptions, propName: 'credit_account' }),
      title: t('creditAccount'),
    },
    {
      dataIndex: 'location',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: locationOptions, propName: 'location' }),
      title: t('location'),
    }
  ];

  return (
    <StyledJournalEntryTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        className='journal-entry-review-tab-filters'
        onFilter={handleOnFilter}
      />
      <EmptyOrTable
        data={data}
        empty={empty}
        loading={loading}
        query={query}
        columns={columns}
        tableProps={tableProps}
      />
    </StyledJournalEntryTabContainer>
  );
}

JournalEntryReviewTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  onAttachmentRemove: PropTypes.func,
  onFilter: PropTypes.func,
  onRowUpdate: PropTypes.func,
  selectedRowsIds: PropTypes.array,
  tableFiltersProps: PropTypes.any,
  tableProps: PropTypes.object
}

const mapStateToProps = ({
  company: { employees },
}) => ({
  employees,
});

export default connect(mapStateToProps, null)(JournalEntryReviewTab);
